"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";

interface HeroProps {
  imageSrc: string;
  imageAlt: string;
  title: string;
  subtitle: string;
  ctaText: string;
  ctaLink: string;
}

const Hero: React.FC<HeroProps> = ({ imageSrc, imageAlt, title, subtitle, ctaText, ctaLink }) => {
  if (!imageSrc || !imageAlt || !title || !subtitle || !ctaText || !ctaLink) {
    throw new Error("Hero component requires imageSrc, imageAlt, title, subtitle, ctaText, and ctaLink props");
  }

  return (
    <section
      className="relative w-full h-[50vh] md:h-[60vh] overflow-hidden bg-gray-900"
      role="banner"
      aria-labelledby="hero-heading"
    >
      <Image
        src={imageSrc}
        alt={imageAlt}
        fill
        className="object-cover brightness-50"
        priority
        sizes="100vw"
      />
      <div className="absolute inset-0 bg-black/30 z-10"></div>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 pt-[88px] z-20">
        <h1
          id="hero-heading"
          className="text-white text-4xl sm:text-5xl md:text-6xl font-bold mb-4 drop-shadow-lg after:content-[''] after:block after:w-[200px] after:h-[3px] after:mt-4 after:mx-auto after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]"
        >
          {title}
        </h1>
        <p className="text-white text-base sm:text-lg md:text-xl mb-6 max-w-2xl drop-shadow-lg">
          {subtitle}
        </p>
        <Link
          href={ctaLink}
          className="
            bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B]
            hover:from-[#ECD05B] hover:to-[#AE7D2F]
            text-white font-semibold py-3 px-6 rounded-lg
            shadow-lg hover:shadow-inner
            [text-shadow:1px_1px_2px_rgba(0,0,0,0.5)]
            hover:scale-105 hover:-translate-y-1
            transition-all duration-300 ease-in-out
          "
          aria-label={ctaText}
        >
          {ctaText}
        </Link>
      </div>
    </section>
  );
};

export default Hero;